/**
 * VehicleLookupBase: shared DOM helpers, step navigation, and search binding for the vehicle lookup section popups only.
 * Used by model-vehicle.js, model-side-by-side.js, model-heavy-equipment.js, model-agriculture-turf.js, model-semis-heavy-duty-trucks.js.
 * Does not affect collection filters or PDP; subclasses own selectedData, data loading, getMatches, redirects, and metafields.
 */
class VehicleLookupBase extends HTMLElement {

    createElement(tag, textContent, attributes = {}) {
        const element = document.createElement(tag);
        if (textContent) element.textContent = textContent;
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'class') {
                element.className = value;
            } else {
                element.setAttribute(key, value);
            }
        });
        return element;
    }

    createListItem(content, attributes = {}) {
        const attrs = Object.entries({
            ...attributes,
            class: attributes.class || 'data-vehicle-model-list'
        }).map(([k, v]) => `${k}="${v}"`).join(' ');
        return `<li ${attrs}>${content}</li>`;
    }

    showElement(element) {
        element?.classList.remove('d-none');
    }

    hideElement(element) {
        element?.classList.add('d-none');
    }

    toggleSubmitButton(button, show) {
        if (!button) return;
        button.classList.toggle('d-none', !show);
    }

    navigateToPreviousStep(button, backStep) {
        const currentStep = button?.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep?.closest('.block__vehicle-lookup-popup-list');
        if (!currentStep || !parentEle) return;
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
        const previousStep = parentEle.querySelector(`[current-step="${backStep}"]`);
        if (previousStep) this.showElement(previousStep);
    }

    navigateToNextStep(element) {
        const currentStep = element?.closest('.block__vehicle-lookup-popup-details');
        if (!currentStep) return;
        const nextStepIndex = currentStep.getAttribute('data-next-step');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        if (!parentEle) return;
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
        const nextStep = parentEle.querySelector(`[current-step="${nextStepIndex}"]`);
        if (nextStep) this.showElement(nextStep);
    }

    bindStepBackEvents() {
        document.addEventListener('click', (e) => {
            const backButton = e.target.closest('[data-step-back-btn]');
            if (!backButton) return;
            e.preventDefault();
            const backStep = backButton.getAttribute('data-back-step');
            if (backStep && backStep !== '0') {
                this.navigateToPreviousStep(backButton, backStep);
            } else {
                const popup = backButton.closest('.block__vehicle-lookup-popup');
                if (popup) popup.classList.add('d-none');
            }
        });
    }

    bindSearchConfig(config) {
        if (!config.input || !config.handler) return;
        let debounceTimer;
        config.input.addEventListener('keyup', (e) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => config.handler(e), 150);
        });
        const clickTarget = config.suggestionBox || config.list;
        clickTarget?.addEventListener('click', config.selectionHandler);
        config.submit?.addEventListener('click', config.submitHandler);
    }

    /**
     * Set text content of an element found by selector within this custom element.
     */
    updateFinalDisplay(selector, content) {
        const element = this.querySelector(selector);
        if (element) element.innerHTML = content;
    }

    /**
     * Load vehicle data via the provided loader and call initVehicle(json). On failure, logs and shows load error message.
     * Subclasses call from constructor: this.initJsonData(), and override initJsonData to call super with their loader,
     * e.g. initJsonData() { return super.initJsonData(() => vehicleUtility.vehicleCSVJSON()); }
     * @param {function(): Promise<object>} loadData - Function that returns a Promise resolving to the vehicle JSON
     */
    initJsonData(loadData) {
        if (typeof loadData !== 'function') return;
        loadData()
            .then(json => this.initVehicle(json))
            .catch(error => {
                console.error('Failed to load vehicle data:', error);
                this.showLoadErrorMessage();
            });
    }

    /**
     * Show user-visible message when vehicle data fails to load. Call from initJsonData catch.
     * Uses theme notification (custom-notification) when available.
     */
    showLoadErrorMessage() {
        if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
            window.notificationEle.updateNotification('Vehicle data', 'Vehicle data couldn\'t be loaded. Please try again later.', { type: 'error', timeout: 8000 });
        }
    }

    /**
     * Populate the first-step suggestion list (make/model or category). Shared by all 5 model files.
     * @param {string[]} items - e.g. uniqueMakeModels or uniqueCategories
     * @param {Object} [options] - limit (default 3), showSuggestionBox (default false), onItemSelect(value, displayText) called when user clicks an item
     */
    populateInitialSuggestions(items, options = {}) {
        const container = this.querySelector('[data-vehicle-suggestion]');
        const suggestionBox = this.querySelector('[data-vehicle-suggestion-box]');
        if (!container || !suggestionBox) return;

        if (options.showSuggestionBox) this.showElement(suggestionBox);
        container.innerHTML = '';

        const limit = options.limit ?? 3;
        const list = (items || []).slice(0, limit);

        list.forEach(item => {
            const displayText = String(item).replace(/\s?\|\s?/g, ' ').trim();
            const li = this.createElement('li', displayText, {
                'data-vehicle-value': '',
                'data-vehicle-model-value': item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            li.addEventListener('click', (e) => {
                container.querySelectorAll('li').forEach(el => el.classList.remove('selected'));
                e.target.classList.add('selected');
                const value = e.target.getAttribute('data-vehicle-model-value');
                const text = e.target.textContent?.trim() || displayText;
                if (typeof options.onItemSelect === 'function') options.onItemSelect(value, text);
                const box = e.target.closest('.block__vehicle-lookup-input-box');
                const input = box?.querySelector('[data-search-vehicle-make-model]');
                const submit = box?.querySelector('[data-search-vehicle-submit]');
                if (input) input.value = text;
                if (submit) submit.classList.remove('d-none');
            });
            container.appendChild(li);
        });
    }

    /**
     * Cache DOM references. selectorMap is { key: 'css-selector', ... }; results go to this.elements.
     */
    cacheElements(selectorMap) {
        this.elements = this.elements || {};
        Object.entries(selectorMap).forEach(([key, selector]) => {
            this.elements[key] = this.querySelector(selector);
        });
    }

    /**
     * Shared search handler. Subclasses must implement getSearchConfig(type), getMatches(type, query), and populateResults(config, matches).
     * Config from getSearchConfig must include { list, box, submit }; optional type and minLength (otherwise getMinLength(type) is used).
     * Optional hooks: beforeHandleSearch(query, config), getMinLengthLabel(type), getMinLengthCustomMessage(type, minLength).
     * @param {Event} e - Input keyup event
     * @param {string} type - Search step type (e.g. 'makeModel', 'year', 'category')
     */
    handleSearch(e, type) {
        const config = this.getSearchConfig(type);
        if (!config || !config.list) return;

        const effectiveType = config.type ?? type;
        const minLength = config.minLength ?? (typeof this.getMinLength === 'function' ? this.getMinLength(type) : 2);
        const query = vehicleUtility.normalizeQuery(e.target.value);
        const inputBox = e.target.closest('.block__vehicle-lookup-input-box');

        inputBox?.classList.toggle('active-value', !!query);
        if (typeof this.beforeHandleSearch === 'function') this.beforeHandleSearch(query, config);

        this.toggleSubmitButton(config.submit, false);
        config.list.innerHTML = '';

        if (!query) {
            this.hideElement(config.box);
            return;
        }

        if (query.length >= minLength) {
            const matches = (this.getMatches(effectiveType, query) ?? []).filter(m => m != null);
            this.populateResults(config, matches);
        } else {
            const label = typeof this.getMinLengthLabel === 'function' ? this.getMinLengthLabel(effectiveType) : effectiveType;
            const customMessage = typeof this.getMinLengthCustomMessage === 'function' ? this.getMinLengthCustomMessage(effectiveType, minLength) : undefined;
            this.showMinLengthMessage(config.list, config.box, minLength, label, effectiveType === 'year', customMessage);
        }
    }

    /**
     * Show the result box and display "enter at least N character(s)/number(s) of the label" message.
     * @param {HTMLElement} container - List element to set innerHTML on
     * @param {HTMLElement} box - Result box to show (d-none removed)
     * @param {number} minLength - Minimum length to display in message
     * @param {string} label - e.g. "vehicle", "year", "cab type", "manufacturer"
     * @param {boolean} isYear - If true, use "number(s)"; otherwise "character(s)"
     * @param {string} [customMessage] - If provided, use this exact message instead of building from label/isYear
     */
    showMinLengthMessage(container, box, minLength, label, isYear, customMessage) {
        this.showElement(box);
        if (customMessage != null) {
            container.innerHTML = `<li class="data-vehicle-model-list">${customMessage}</li>`;
            return;
        }
        const unit = isYear
            ? (minLength > 1 ? 'numbers' : 'number')
            : (minLength > 1 ? 'characters' : 'character');
        container.innerHTML = `<li class="data-vehicle-model-list">Please enter at least ${minLength} ${unit} of the ${label}.</li>`;
    }
}

window.VehicleLookupBase = VehicleLookupBase;

/**
 * Vehicle Lookup section: open/close popups when a vehicle type button is clicked.
 * Scoped to .block__vehicle-lookup-section so it only runs when the section is present.
 */
(function () {
    function initVehicleLookupPopups() {
        const section = document.querySelector('.block__vehicle-lookup-section');
        if (!section) return;

        section.querySelectorAll('[data-vehicle-button]').forEach(button => {
            button.addEventListener('click', async () => {
                const vehicleType = button.getAttribute('data-vehicle-type');
                if (!vehicleType) return;
                await vehicleUtility.getOrLoadByType(vehicleType);
                section.querySelectorAll('.block__vehicle-lookup-popup').forEach(popup => {
                    popup.classList.add('d-none');
                });
                const vehiclePopup = section.querySelector('.block__vehicle-lookup-popup[data-type="shop-' + vehicleType + '"]');
                if (vehiclePopup) {
                    vehiclePopup.classList.remove('d-none');
                    const list = vehiclePopup.querySelector('.block__vehicle-lookup-popup-list');
                    if (list) {
                        list.classList.remove('d-none');
                        if (typeof list.initFromGlobal === 'function') list.initFromGlobal();
                    }
                }
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initVehicleLookupPopups);
    } else {
        initVehicleLookupPopups();
    }
})();
